import React, { useRef, useEffect, useCallback, useState } from 'react'
import { ChevronLeft, ChevronRight, Plus, Camera, ImagePlus } from 'lucide-react'
import { cn } from '@/utils/cn'
import { RoomSection, HistoryItem } from '@/types'
import { SectionWithImages } from '@/utils/sectionStatus'
import MobileSectionCard from './MobileSectionCard'

export interface ValidationCounts {
  match: number
  mismatch: number
  missing: number
  unexpected: number
}

interface MobileSectionCarouselProps {
  sections: RoomSection[]
  sectionsWithStatus: SectionWithImages[]
  currentIndex: number
  isSessionCompleted: boolean
  onIndexChange: (index: number) => void
  onTakePhoto: (sectionId: number) => void
  onChooseGallery: (sectionId: number) => void
  onViewDetails: (section: RoomSection) => void
  onReupload?: (sectionId: number) => void
  onViewDetections?: (sectionIndex: number) => void
  onViewValidation?: (sectionId: number) => void
  loadAnalysisImageUrl?: (analysisId: number) => string | null
  // Recently uploaded images by sectionId - for immediate thumbnail update
  recentUploadBySectionId?: Record<number, string>
  // For creating new sections
  onCreateSectionTakePhoto?: () => void
  onCreateSectionChooseGallery?: () => void
  // Validation counts per section
  getValidationCounts?: (sectionId: number) => ValidationCounts | null
}

const MobileSectionCarousel: React.FC<MobileSectionCarouselProps> = ({
  sections,
  sectionsWithStatus,
  currentIndex,
  isSessionCompleted,
  onIndexChange,
  onTakePhoto,
  onChooseGallery,
  onViewDetails,
  onReupload,
  onViewDetections,
  onViewValidation,
  loadAnalysisImageUrl,
  recentUploadBySectionId,
  onCreateSectionTakePhoto,
  onCreateSectionChooseGallery,
  getValidationCounts,
}) => {
  const scrollRef = useRef<HTMLDivElement>(null)
  const [containerWidth, setContainerWidth] = useState(0)
  const isInitialMount = useRef(true)
  const ignoreScrollEvents = useRef(true) // Ignore scroll events until after initial positioning
  const prevIndex = useRef(currentIndex)
  const [isReady, setIsReady] = useState(false) // Don't enable scrolling until positioned

  // Show "create new section" card if not completed and callbacks are provided
  const showCreateCard = !isSessionCompleted && onCreateSectionTakePhoto && onCreateSectionChooseGallery
  const totalSlides = sections.length + (showCreateCard ? 1 : 0)
  const isOnCreateCard = currentIndex === sections.length && showCreateCard

  // Disable browser scroll restoration for this component
  useEffect(() => {
    if ('scrollRestoration' in history) {
      const original = history.scrollRestoration
      history.scrollRestoration = 'manual'
      return () => {
        history.scrollRestoration = original
      }
    }
  }, [])

  // Measure container width
  useEffect(() => {
    const updateWidth = () => {
      if (scrollRef.current) {
        setContainerWidth(scrollRef.current.clientWidth)
      }
    }
    updateWidth()
    window.addEventListener('resize', updateWidth)
    return () => window.removeEventListener('resize', updateWidth)
  }, [])

  // Scroll to current index - instant on mount, smooth on navigation
  useEffect(() => {
    if (scrollRef.current && containerWidth > 0) {
      const scrollPosition = currentIndex * containerWidth

      if (isInitialMount.current) {
        // Initial mount: set position without animation, then enable scrolling
        scrollRef.current.scrollLeft = scrollPosition
        requestAnimationFrame(() => {
          if (scrollRef.current) {
            scrollRef.current.scrollLeft = scrollPosition
          }
          setIsReady(true)
          requestAnimationFrame(() => {
            if (scrollRef.current) {
              scrollRef.current.scrollLeft = scrollPosition
            }
            setTimeout(() => {
              ignoreScrollEvents.current = false
            }, 100)
          })
        })
        isInitialMount.current = false
      } else {
        // Normal navigation - use smooth scroll
        const useSmooth = prevIndex.current !== currentIndex
        scrollRef.current.scrollTo({
          left: scrollPosition,
          behavior: useSmooth ? 'smooth' : 'instant',
        })
      }

      prevIndex.current = currentIndex
    }
  }, [currentIndex, containerWidth])

  // Handle scroll end to detect index change
  const handleScrollEnd = useCallback(() => {
    // Ignore scroll events during initial mount (prevents browser scroll restoration from changing index)
    if (ignoreScrollEvents.current) return

    if (scrollRef.current && containerWidth > 0) {
      const scrollLeft = scrollRef.current.scrollLeft
      const newIndex = Math.round(scrollLeft / containerWidth)
      if (newIndex !== currentIndex && newIndex >= 0 && newIndex < totalSlides) {
        onIndexChange(newIndex)
      }
    }
  }, [currentIndex, totalSlides, onIndexChange, containerWidth])

  // Debounced scroll handler
  const scrollTimeoutRef = useRef<ReturnType<typeof setTimeout>>()
  const handleScroll = useCallback(() => {
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current)
    }
    scrollTimeoutRef.current = setTimeout(handleScrollEnd, 150)
  }, [handleScrollEnd])

  const goToPrevious = () => {
    if (currentIndex > 0) {
      onIndexChange(currentIndex - 1)
    }
  }

  const goToNext = () => {
    if (currentIndex < totalSlides - 1) {
      onIndexChange(currentIndex + 1)
    }
  }

  // Get section status data
  const getSectionData = (section: RoomSection) => {
    const sectionData = sectionsWithStatus.find((s) => s.section.id === section.id)
    const images = sectionData?.images || []
    const latestImage = images.length > 0 ? images[0] : null

    // Get the uploaded image URL if available
    let uploadedImageUrl: string | null = null

    // Priority 1: Check recent uploads by sectionId (immediate update after upload)
    if (recentUploadBySectionId?.[section.id]) {
      uploadedImageUrl = recentUploadBySectionId[section.id]
    }
    // Priority 2: Check cached URLs by analysisId
    else if (latestImage) {
      // Try to get loaded image URL, or use visualization_path as fallback
      if (loadAnalysisImageUrl) {
        uploadedImageUrl = loadAnalysisImageUrl(latestImage.id)
      }
      if (!uploadedImageUrl && latestImage.visualization_path) {
        uploadedImageUrl = latestImage.visualization_path
      }
    }

    return {
      status: sectionData?.status || 'empty',
      images,
      imageCount: images.length,
      detectionCount: images.reduce(
        (sum, img) => sum + (img.detections?.length || 0),
        0
      ),
      uploadedImageUrl,
      latestImage,
    }
  }

  return (
    <div className="flex flex-col h-full">
      {/* Section Counter */}
      <div className="flex items-center justify-between px-4 py-2 flex-shrink-0">
        <span className="text-sm text-gray-500">
          {isOnCreateCard ? (
            'Create New Section'
          ) : (
            `Section ${currentIndex + 1} of ${sections.length}`
          )}
        </span>
        <div className="flex items-center gap-2">
          <button
            onClick={goToPrevious}
            disabled={currentIndex === 0}
            className={cn(
              'p-2 rounded-full transition-colors',
              currentIndex === 0
                ? 'text-gray-300'
                : 'text-gray-600 active:bg-gray-100'
            )}
            aria-label="Previous section"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
          <button
            onClick={goToNext}
            disabled={currentIndex === totalSlides - 1}
            className={cn(
              'p-2 rounded-full transition-colors',
              currentIndex === totalSlides - 1
                ? 'text-gray-300'
                : 'text-gray-600 active:bg-gray-100'
            )}
            aria-label="Next section"
          >
            <ChevronRight className="h-5 w-5" />
          </button>
        </div>
      </div>

      {/* Carousel Container - Full width single card */}
      <div
        ref={scrollRef}
        onScroll={handleScroll}
        className="flex-1 flex scrollbar-hide"
        style={{
          overflowX: isReady ? 'auto' : 'hidden',
          scrollSnapType: isReady ? 'x mandatory' : 'none',
          WebkitOverflowScrolling: 'touch',
        }}
      >
        {sections.map((section, index) => {
          const { status, imageCount, detectionCount, uploadedImageUrl } = getSectionData(section)

          return (
            <div
              key={section.id}
              className="snap-center flex-shrink-0 w-full px-4"
              style={{ scrollSnapAlign: 'center' }}
            >
              <MobileSectionCard
                section={section}
                status={status}
                imageCount={imageCount}
                detectionCount={detectionCount}
                isActive={index === currentIndex}
                isSessionCompleted={isSessionCompleted}
                uploadedImageUrl={uploadedImageUrl}
                validationCounts={getValidationCounts?.(section.id) ?? null}
                onTakePhoto={() => onTakePhoto(section.id)}
                onChooseGallery={() => onChooseGallery(section.id)}
                onViewDetails={() => onViewDetails(section)}
                onReupload={onReupload ? () => onReupload(section.id) : undefined}
                onViewDetections={onViewDetections ? () => onViewDetections(index) : undefined}
                onViewValidation={onViewValidation ? () => onViewValidation(section.id) : undefined}
              />
            </div>
          )
        })}

        {/* Create New Section Card */}
        {showCreateCard && (
          <div
            key="create-new-section"
            className="snap-center flex-shrink-0 w-full px-4"
            style={{ scrollSnapAlign: 'center' }}
          >
            <div
              className={cn(
                'flex flex-col bg-white rounded-2xl border-2 border-dashed shadow-sm overflow-hidden h-full',
                'w-full',
                isOnCreateCard ? 'border-primary-400' : 'border-gray-300'
              )}
            >
              {/* Header */}
              <div className="px-4 py-3 border-b bg-gray-50/80">
                <h3 className="font-semibold text-gray-900 text-lg">
                  Create New Section
                </h3>
                <p className="text-sm text-gray-500 mt-0.5">
                  Upload a photo to create a new section
                </p>
              </div>

              {/* Content */}
              <div className="flex-1 flex flex-col items-center justify-center p-6">
                <div className="w-20 h-20 rounded-full bg-primary-100 flex items-center justify-center mb-4">
                  <Plus className="h-10 w-10 text-primary-600" />
                </div>
                <p className="text-gray-600 text-center mb-6">
                  Take a photo or choose from gallery to automatically create a new section for this room
                </p>
              </div>

              {/* Actions */}
              <div className="px-4 pb-4 space-y-2">
                <button
                  onClick={onCreateSectionTakePhoto}
                  className={cn(
                    'w-full flex items-center justify-center gap-2 py-2.5 rounded-xl',
                    'bg-primary-600 text-white font-medium',
                    'active:bg-primary-700 transition-colors'
                  )}
                >
                  <Camera className="h-5 w-5" />
                  Take Photo
                </button>
                <button
                  onClick={onCreateSectionChooseGallery}
                  className={cn(
                    'w-full flex items-center justify-center gap-2 py-2.5 rounded-xl',
                    'bg-white border-2 border-gray-300 text-gray-700 font-medium',
                    'active:bg-gray-50 transition-colors'
                  )}
                >
                  <ImagePlus className="h-5 w-5" />
                  Choose from Gallery
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Dot Indicators */}
      <div className="flex justify-center gap-2 py-3 flex-shrink-0">
        {sections.map((section, index) => {
          const { status } = getSectionData(section)
          const dotColor =
            status === 'complete'
              ? 'bg-emerald-500'
              : status === 'mismatch'
              ? 'bg-amber-500'
              : 'bg-gray-300'

          return (
            <button
              key={section.id}
              onClick={() => onIndexChange(index)}
              className={cn(
                'w-2.5 h-2.5 rounded-full transition-all',
                dotColor,
                index === currentIndex && 'scale-125 ring-2 ring-offset-2 ring-primary-400'
              )}
              aria-label={`Go to section ${index + 1}`}
            />
          )
        })}
        {/* Create New Section dot */}
        {showCreateCard && (
          <button
            onClick={() => onIndexChange(sections.length)}
            className={cn(
              'w-2.5 h-2.5 rounded-full transition-all bg-primary-300',
              isOnCreateCard && 'scale-125 ring-2 ring-offset-2 ring-primary-400'
            )}
            aria-label="Create new section"
          >
            <Plus className="w-full h-full text-primary-600" />
          </button>
        )}
      </div>
    </div>
  )
}

export default MobileSectionCarousel
